/**
 * What a queued write looks like, how it becomes an HTTP request, and how it is
 * shown on the tablet before the Worker has seen it.
 *
 * One vocabulary for both paths: the online call and the outbox replay build
 * the same `Op`, so nothing is sent on sync that could not have been sent live.
 *
 * `applyLocally` patches the cached DayView so the clerk sees what she typed.
 * It is deliberately dumb about money: the Worker does every division and
 * rounding (CLAUDE.md rule 12), so a queued row keeps the figures it had and
 * wears a `queued` flag until the real ones come back. Pure addition — an
 * advance onto the day's advances, an expense onto the misc total — is done,
 * because that is what a generated column would do. The one pay rule it does
 * not reproduce is hours-over-8-become-overtime: 10 typed offline stays 10 on
 * screen and becomes 8 + 2 when it syncs. Copying the rule here would be a
 * second place for it to be wrong.
 */

import type {
  AdvanceRow, DayView, EntryPatch, EntryRow, MiscRow, SectionName,
} from './day';
import { isSundayDate } from './calendar';

export type Op =
  | { kind: 'entry.patch'; date: string; workerId: string; patch: EntryPatch; rowVersion: number }
  | { kind: 'entry.finalise'; date: string; workerId: string }
  | { kind: 'entry.unfinalise'; date: string; workerId: string }
  | {
      kind: 'advance.add'; date: string; workerId: string; advanceId: string;
      amountRs: number; note: string | null; payWeekEnding: string; paidByName: string;
    }
  | { kind: 'advance.void'; date: string; advanceId: string; reason: string | null }
  | { kind: 'signature.put'; date: string; advanceId: string }
  | { kind: 'rate.set'; date: string; dieselRateRs: number }
  | { kind: 'fuel.set'; date: string; plantId: string; litresX100: number }
  | { kind: 'fuel.code'; date: string; plantId: string; costCodeId: string | null }
  | {
      kind: 'misc.add'; date: string; miscId: string; description: string; amountRs: number;
      categoryId: string; categoryName: string; requiresPlant: number; plantId: string | null;
      /** Absent on an op queued before cost codes: the Worker then uses the plant's. */
      costCodeId?: string | null;
    }
  | {
      kind: 'misc.update'; date: string; miscId: string; rowVersion: number;
      patch: {
        description?: string; amountRs?: number; categoryId?: string; categoryName?: string;
        requiresPlant?: number; plantId?: string | null; costCodeId?: string | null;
      };
    }
  | { kind: 'misc.delete'; date: string; miscId: string }
  | { kind: 'receipt.put'; date: string; miscId: string }
  | { kind: 'section.submit'; date: string; section: SectionName; overrideUnsigned: boolean }
  | { kind: 'section.pullback'; date: string; section: SectionName };

export interface QueuedOp {
  /** UUIDv7 — the x-client-op-id it is sent with, live or on replay. */
  id: string;
  at: string;
  /** What the clerk sees in the queue and in a problem: "Sunil Perera — advance Rs 2,000". */
  label: string;
  op: Op;
  /** A signature PNG or a receipt JPEG, sent as the raw body. */
  blob?: Blob;
  contentType?: string;
}

export interface Wire {
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  body?: unknown;
}

/** The HTTP request an op is, live or replayed. */
export function toWire(op: Op): Wire {
  switch (op.kind) {
    case 'entry.patch':
      return {
        method: 'PATCH',
        path: `/days/${op.date}/entries/${op.workerId}`,
        body: { ...op.patch, rowVersion: op.rowVersion },
      };
    case 'entry.finalise':
      return { method: 'POST', path: `/days/${op.date}/entries/${op.workerId}/finalise` };
    case 'entry.unfinalise':
      return { method: 'DELETE', path: `/days/${op.date}/entries/${op.workerId}/finalise` };
    case 'advance.add':
      return {
        method: 'POST',
        path: `/days/${op.date}/entries/${op.workerId}/advances`,
        body: { id: op.advanceId, amountRs: op.amountRs, note: op.note },
      };
    case 'advance.void':
      return { method: 'DELETE', path: `/advances/${op.advanceId}`, body: { reason: op.reason } };
    case 'signature.put':
      return { method: 'POST', path: `/advances/${op.advanceId}/signature` };
    case 'rate.set':
      return { method: 'PUT', path: `/days/${op.date}/rate`, body: { dieselRateRs: op.dieselRateRs } };
    case 'fuel.set':
      return { method: 'PUT', path: `/days/${op.date}/fuel/${op.plantId}`, body: { litresX100: op.litresX100 } };
    case 'fuel.code':
      return { method: 'PUT', path: `/days/${op.date}/fuel/${op.plantId}`, body: { costCodeId: op.costCodeId } };
    case 'misc.add':
      return {
        method: 'POST',
        path: `/days/${op.date}/misc`,
        body: {
          id: op.miscId, description: op.description, amountRs: op.amountRs,
          categoryId: op.categoryId, plantId: op.plantId,
          ...(op.costCodeId !== undefined ? { costCodeId: op.costCodeId } : {}),
        },
      };
    case 'misc.update': {
      const { categoryName: _n, requiresPlant: _r, ...sent } = op.patch;
      return { method: 'PATCH', path: `/misc/${op.miscId}`, body: { ...sent, rowVersion: op.rowVersion } };
    }
    case 'misc.delete':
      return { method: 'DELETE', path: `/misc/${op.miscId}` };
    case 'receipt.put':
      return { method: 'POST', path: `/misc/${op.miscId}/receipt` };
    case 'section.submit':
      return {
        method: 'POST',
        path: `/days/${op.date}/sections/${op.section}/submit`,
        body: { overrideUnsigned: op.overrideUnsigned },
      };
    case 'section.pullback':
      return { method: 'DELETE', path: `/days/${op.date}/sections/${op.section}/submit` };
  }
}

// ------------------------------------------------------- the reducer

/** The cached view with one queued op laid on top. Never mutates its input. */
export function applyLocally(view: DayView, op: Op, at: string): DayView {
  const next: DayView = {
    ...view,
    day: { ...view.day },
    sections: view.sections.map((row) => ({ ...row })),
    entries: view.entries.map((row) => ({ ...row, advances: row.advances.map((a) => ({ ...a })) })),
    fuel: view.fuel.map((row) => ({ ...row })),
    misc: view.misc.map((row) => ({ ...row })),
    totals: { ...view.totals },
    queued: (view.queued ?? 0) + 1,
  };
  // The first write is what opens a day on the server, too.
  next.day.opened = true;

  const entry = (workerId: string): EntryRow => {
    const row = next.entries.find((e) => e.workerId === workerId);
    if (!row) throw new Error('That worker is not on this day.');
    return row;
  };
  const misc = (miscId: string): MiscRow => {
    const row = next.misc.find((m) => m.id === miscId);
    if (!row) throw new Error('That expense is not on this day.');
    return row;
  };

  switch (op.kind) {
    case 'entry.patch': {
      const row = entry(op.workerId);
      Object.assign(row, op.patch);
      if (op.patch.attendance === 'absent') {
        Object.assign(row, {
          hoursTenths: 0, otTenths: 0, allowanceRs: 0, sundayAllowanceRs: 0,
          salaryRs: 0, otRs: 0, earnedRs: 0, baseEarnedRs: 0,
        });
      }
      row.rowVersion += 1;
      row.queued = true;
      break;
    }
    case 'entry.finalise': {
      const row = entry(op.workerId);
      row.finalised = true;
      row.rejected = false;
      row.rowVersion += 1;
      row.queued = true;
      break;
    }
    case 'entry.unfinalise': {
      const row = entry(op.workerId);
      row.finalised = false;
      row.rowVersion += 1;
      row.queued = true;
      break;
    }
    case 'advance.add': {
      const row = entry(op.workerId);
      const advance: AdvanceRow = {
        id: op.advanceId, amountRs: op.amountRs, note: op.note, payWeekEnding: op.payWeekEnding,
        signed: false, signedAt: null, paidByName: op.paidByName, rowVersion: 1, queued: true,
      };
      row.advances.push(advance);
      row.advancesThisWeekRs += op.amountRs;
      next.totals.advancesRs += op.amountRs;
      next.totals.cashOutRs += op.amountRs;
      row.queued = true;
      break;
    }
    case 'advance.void': {
      for (const row of next.entries) {
        const found = row.advances.find((a) => a.id === op.advanceId);
        if (!found) continue;
        row.advances = row.advances.filter((a) => a.id !== op.advanceId);
        row.advancesThisWeekRs -= found.amountRs;
        next.totals.advancesRs -= found.amountRs;
        next.totals.cashOutRs -= found.amountRs;
        row.queued = true;
      }
      break;
    }
    case 'signature.put': {
      for (const row of next.entries) {
        const found = row.advances.find((a) => a.id === op.advanceId);
        if (!found) continue;
        found.signed = true;
        found.signedAt = at;
        found.queued = true;
      }
      break;
    }
    case 'rate.set':
      next.day.dieselRateRs = op.dieselRateRs;
      next.day.rowVersion += 1;
      // Every litre on the day follows the price; the rupee value is the
      // Worker's to round, so the rows show as queued until it answers.
      for (const row of next.fuel) {
        if (row.rate_rs === op.dieselRateRs) continue;
        row.rate_rs = op.dieselRateRs;
        row.row_version += 1;
        row.queued = true;
      }
      break;
    case 'fuel.set': {
      const existing = next.fuel.find((f) => f.plant_id === op.plantId);
      // Litres are pure addition, so the day's total can follow at once; the
      // rupee value waits for the Worker like the row's own value_rs.
      next.totals.litresX100 += op.litresX100 - (existing?.litres_x100 ?? 0);
      if (existing) {
        existing.litres_x100 = op.litresX100;
        existing.rate_rs = next.day.dieselRateRs;
        existing.row_version += 1;
        existing.queued = true;
      } else {
        // value_rs is litres × rate, rounded — the Worker's to do. Until then
        // the row shows its litres and no value.
        next.fuel.push({
          plant_id: op.plantId, litres_x100: op.litresX100, rate_rs: next.day.dieselRateRs,
          value_rs: 0, row_version: 1, queued: true,
        });
      }
      break;
    }
    case 'fuel.code': {
      const existing = next.fuel.find((f) => f.plant_id === op.plantId);
      if (existing) {
        existing.cost_code_id = op.costCodeId;
        existing.row_version += 1;
        existing.queued = true;
      } else {
        next.fuel.push({
          plant_id: op.plantId, litres_x100: 0, rate_rs: next.day.dieselRateRs,
          value_rs: 0, cost_code_id: op.costCodeId, row_version: 1, queued: true,
        });
      }
      break;
    }
    case 'misc.add': {
      next.misc.push({
        id: op.miscId, description: op.description, amount_rs: op.amountRs,
        category_id: op.categoryId, category_name: op.categoryName, requires_plant: op.requiresPlant,
        plant_id: op.plantId, cost_code_id: op.costCodeId ?? null, receipt_key: null,
        row_version: 1, queued: true,
      });
      next.totals.miscRs += op.amountRs;
      next.totals.costRs += op.amountRs;
      next.totals.cashOutRs += op.amountRs;
      break;
    }
    case 'misc.update': {
      const row = misc(op.miscId);
      const before = row.amount_rs;
      if (op.patch.description !== undefined) row.description = op.patch.description;
      if (op.patch.amountRs !== undefined) row.amount_rs = op.patch.amountRs;
      if (op.patch.categoryId !== undefined) row.category_id = op.patch.categoryId;
      if (op.patch.categoryName !== undefined) row.category_name = op.patch.categoryName;
      if (op.patch.requiresPlant !== undefined) row.requires_plant = op.patch.requiresPlant;
      if (op.patch.plantId !== undefined) row.plant_id = op.patch.plantId;
      if (op.patch.costCodeId !== undefined) row.cost_code_id = op.patch.costCodeId;
      const delta = row.amount_rs - before;
      next.totals.miscRs += delta;
      next.totals.costRs += delta;
      next.totals.cashOutRs += delta;
      row.row_version += 1;
      row.queued = true;
      break;
    }
    case 'misc.delete': {
      const row = misc(op.miscId);
      next.misc = next.misc.filter((m) => m.id !== op.miscId);
      next.totals.miscRs -= row.amount_rs;
      next.totals.costRs -= row.amount_rs;
      next.totals.cashOutRs -= row.amount_rs;
      break;
    }
    case 'receipt.put': {
      const row = misc(op.miscId);
      row.receipt_key = 'queued';
      row.queued = true;
      break;
    }
    case 'section.submit': {
      const row = next.sections.find((s) => s.section === op.section);
      if (row) {
        row.status = 'submitted';
        row.submittedAt = at;
        row.rowVersion += 1;
        row.queued = true;
      }
      break;
    }
    case 'section.pullback': {
      const row = next.sections.find((s) => s.section === op.section);
      if (row) {
        row.status = 'open';
        row.submittedAt = null;
        row.rowVersion += 1;
        row.queued = true;
      }
      break;
    }
  }

  return next;
}

/** A day never loaded while there was signal, built from the last one that was. */
export function synthesiseDay(date: string, from: DayView): DayView {
  return {
    day: {
      workDate: date,
      isSunday: isSundayDate(date),
      dieselRateRs: from.day.dieselRateRs,
      hasData: false,
      closedAt: null,
      opened: false,
      rowVersion: 1,
    },
    sections: (['labour', 'diesel', 'misc'] as const).map((section) => ({
      section, status: 'open', submittedAt: null, approvedAt: null, unsignedOverrideAt: null, rowVersion: 1,
    })),
    entries: from.entries.map((row) => ({
      ...row,
      attendance: null,
      hoursTenths: 0, otTenths: 0, allowanceRs: 0, sundayAllowanceRs: 0, allowanceReason: null,
      salaryRs: 0, otRs: 0, earnedRs: 0, baseEarnedRs: 0,
      status: 'draft', finalised: false, rejected: false, rejectNote: null, rowVersion: 1,
      advancesThisWeekRs: 0, advances: [],
    })),
    fuel: [],
    misc: [],
    totals: {
      labourRs: 0, dieselRs: 0, litresX100: 0, miscRs: 0, advancesRs: 0,
      costRs: 0, cashOutRs: 0, workersOnSite: 0, workersAbsent: 0,
    },
    blockingDay: null,
    synthesised: true,
  };
}
